/**
 * useLiveTotalSharesSold
 *
 * Public hook (no auth required) that fetches the LIVE total allocated shares
 * across ALL phases from /api/public/share-calculations. This is the canonical
 * shares-sold denominator used by every dividend calculator surface that needs
 * to apply the DYNAMIC denominator for the Jul 2026 / Oct 2026 / Jan 2027
 * early-distribution payouts.
 *
 * The endpoint is the same one NewsTickerBar uses, so the calculator and the
 * marquee ticker stay in lockstep — there is exactly one server-side source of
 * truth for "shares sold so far".
 *
 * Returns:
 *   totalSharesSold: number — live allocated shares (base + conversions + commissions)
 *   loading: boolean
 *   error: string | null
 *   refresh: () => Promise<void>
 */
import { useCallback, useEffect, useState } from 'react';

export interface LiveTotalSharesSold {
 totalSharesSold: number;
 loading: boolean;
 error: string | null;
 refresh: () => Promise<void>;
}

export function useLiveTotalSharesSold(): LiveTotalSharesSold {
 const [totalSharesSold, setTotalSharesSold] = useState<number>(0);
 const [loading, setLoading] = useState<boolean>(true);
 const [error, setError] = useState<string | null>(null);

 const fetchTotal = useCallback(async () => {
  try {
   setError(null);
   const res = await fetch('/api/public/share-calculations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ operation: 'get_total_allocated_shares_all_phases' }),
   });
   const result = await res.json();
   if (res.ok && result?.data) {
    const allocated = Number(result.data.totalAllocated) || 0;
    setTotalSharesSold(allocated);
   } else {
    setError(result?.error || `HTTP ${res.status}`);
    setTotalSharesSold(0);
   }
  } catch (e: any) {
   setError(e?.message || 'Network error');
   setTotalSharesSold(0);
  } finally {
   setLoading(false);
  }
 }, []);

 useEffect(() => {
  fetchTotal();
 }, [fetchTotal]);

 return { totalSharesSold, loading, error, refresh: fetchTotal };
}
